<template>
  <transition name="fade">
    <div v-if="isActive" class="guide-container">
      <!-- 半透明蒙层 -->
      <div class="guide-overlay"></div>

      <!-- 高亮元素框 -->
      <div
          v-if="currentStep.target"
          ref="highlightBox"
          class="highlight-box"
          :style="highlightStyle"
      ></div>

      <!-- 提示框 -->
      <div
          class="tooltip"
          :style="tooltipStyle"
          :class="[`tooltip-${currentStep.position || 'bottom'}`]"
      >
        <div class="tooltip-content">
          <div class="tooltip-text">{{ currentStep.text }}</div>
          <div class="tooltip-footer">
            <span class="step-counter">{{ currentStepIndex + 1 }}/{{ steps.length }}</span>
            <div class="tooltip-buttons">
              <button
                  v-if="currentStepIndex < steps.length - 1"
                  class="btn-skip"
                  @click="skipGuide"
              >
                {{ $t("studentProgramme.guide4") }}
              </button>
              <button
                  class="btn-next"
                  @click="nextStep"
              >
                {{ currentStepIndex === steps.length - 1 ? $t("studentProgramme.guide5") : $t("studentProgramme.guide6") }}
              </button>
            </div>
          </div>
        </div>
        <div class="tooltip-arrow"></div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'Guide',
  props: {
    // 引导步骤配置
    steps: {
      type: Array,
      required: true,
      validator: (steps) => {
        return steps.every(step => {
          return step.target && step.text
        })
      }
    },
    // 本地存储的key
    // storageKey: {
    //   type: String,
    //   default: 'false'
    // },
    // 是否强制显示（忽略本地存储）
    forceShow: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isActive: false,
      currentStepIndex: 0,
      observer: null,
      storageKey: 'user_guide_completed'
    }
  },
  computed: {
    currentStep() {
      return this.steps[this.currentStepIndex] || {}
    },
    highlightStyle() {
      const target = document.querySelector(this.currentStep.target)
      if (!target) return { display: 'none' }

      const rect = target.getBoundingClientRect()
      return {
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        top: `${rect.top}px`,
        left: `${rect.left}px`,
      }
    },
    tooltipStyle() {
      const target = document.querySelector(this.currentStep.target)
      if (!target) return {}

      const rect = target.getBoundingClientRect()
      const padding = 20
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight

      // 默认位置计算
      let top = rect.bottom + padding
      let left = rect.left
      let arrowPosition = 'top'

      // 根据位置调整提示框
      switch (this.currentStep.position) {
        case 'top':
          top = rect.top - padding
          left = rect.left
          arrowPosition = 'bottom'
          break
        case 'left':
          top = rect.top
          left = rect.left - padding
          arrowPosition = 'right'
          break
        case 'right':
          top = rect.top
          left = rect.right + padding
          arrowPosition = 'left'
          break
        case 'bottom':
        default:
          top = rect.bottom + padding
          left = rect.left
          arrowPosition = 'top'
      }

      // 边界检查
      if (left < padding) left = padding
      if (left > windowWidth - 300) left = windowWidth - 300 - padding
      if (top < padding) top = padding
      if (top > windowHeight - 150) top = windowHeight - 150 - padding

      return {
        top: `${top}px`,
        left: `${left}px`,
        '--arrow-position': arrowPosition
      }
    }
  },
  methods: {
    startGuide() {
      if (localStorage.getItem(this.storageKey) == 'true') {
        return
      }
      this.isActive = true
      this.$nextTick(() => {
        this.observeTarget()
      })
    },
    nextStep() {
      if (this.currentStepIndex < this.steps.length - 1) {
        this.currentStepIndex++
        this.$nextTick(() => {
          this.observeTarget()
        })
      } else {
        this.completeGuide()
      }
    },
    skipGuide() {
      this.completeGuide()
    },
    completeGuide() {
      this.isActive = false
      localStorage.setItem(this.storageKey, 'true')
      this.$emit('complete')
    },
    observeTarget() {
      if (this.observer) {
        this.observer.disconnect()
      }

      const target = document.querySelector(this.currentStep.target)
      if (!target) {
        console.warn(`Guide target not found: ${this.currentStep.target}`)
        return
      }

      // 使用 MutationObserver 监听目标元素变化
      this.observer = new MutationObserver(() => {
        this.$forceUpdate()
      })

      this.observer.observe(target, {
        attributes: true,
        childList: true,
        subtree: true
      })
    },
    handleResize() {
      this.$forceUpdate()
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.startGuide()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.guide-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
}

.guide-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /*background-color: rgba(0, 0, 0, 0.7);*/
}

.highlight-box {
  position: absolute;
  border: 2px solid #4a90e2;
  border-radius: 4px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
  transition: all 0.3s ease;
  pointer-events: none;
  z-index: 1;
}

.tooltip {
  position: absolute;
  max-width: 300px;
  min-width: 200px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 2;
  animation: fadeIn 0.3s ease;
}

.tooltip-content {
  padding: 16px;
}

.tooltip-text {
  margin-bottom: 12px;
  color: #333;
  line-height: 1.5;
}

.tooltip-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.step-counter {
  color: #999;
  font-size: 12px;
}

.tooltip-buttons {
  display: flex;
  gap: 8px;
}

.btn-skip {
  padding: 6px 12px;
  background: #f0f0f0;
  border: none;
  border-radius: 4px;
  color: #666;
  cursor: pointer;
}

.btn-next {
  padding: 6px 12px;
  background: #fcc03a;
  border: none;
  border-radius: 4px;
  color: black;
  cursor: pointer;
}

.tooltip-arrow {
  position: absolute;
  width: 10px;
  height: 10px;
  background: white;
  transform: rotate(45deg);
}

.tooltip-top .tooltip-arrow {
  bottom: -5px;
  left: 50%;
  margin-left: -5px;
}

.tooltip-bottom .tooltip-arrow {
  top: -5px;
  left: 50%;
  margin-left: -5px;
}

.tooltip-left .tooltip-arrow {
  right: -5px;
  top: 50%;
  margin-top: -5px;
}

.tooltip-right .tooltip-arrow {
  left: -5px;
  top: 50%;
  margin-top: -5px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>